import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Download, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import QRCode from "qrcode";
import { API } from "@/lib/api";

export const PublicInvoiceView = () => {
  const { id } = useParams();
  const printRef = useRef(null);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");

  useEffect(() => {
    const link = invoice?.payment_link || "https://razorpay.me/@pokus";
    QRCode.toDataURL(link, {
      width: 120,
      margin: 1,
      color: { dark: "#0047FF", light: "#ffffff" },
    })
      .then(setQrCodeDataUrl)
      .catch(console.error);
  }, [invoice?.payment_link]);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const response = await fetch(`${API}/invoices/${id}`);
        if (!response.ok) {
          throw new Error("Invoice not found");
        }
        const data = await response.json();
        setInvoice(data);
      } catch (err) {
        console.error("Error fetching invoice:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [id]);

  const generatePDF = async () => {
    if (!printRef.current) return null;

    await new Promise((resolve) => setTimeout(resolve, 100));

    const canvas = await html2canvas(printRef.current, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const imgWidth = 210;
    const pageHeight = 297;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    if (invoice?.payment_link) {
      const paymentLinkEl = printRef.current.querySelector("[data-testid='payment-link']");
      if (paymentLinkEl) {
        const containerRect = printRef.current.getBoundingClientRect();
        const linkRect = paymentLinkEl.getBoundingClientRect();
        const scaleX = imgWidth / containerRect.width;
        const scaleY = imgHeight / containerRect.height;
        const linkX = (linkRect.left - containerRect.left) * scaleX;
        const linkY = (linkRect.top - containerRect.top) * scaleY;
        const linkW = linkRect.width * scaleX;
        const linkH = linkRect.height * scaleY;
        const linkPage = Math.floor(linkY / pageHeight);
        const linkYOnPage = linkY - linkPage * pageHeight;
        pdf.setPage(linkPage + 1);
        pdf.link(linkX, linkYOnPage, linkW, linkH, { url: invoice.payment_link });
      }
    }

    return pdf;
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const pdf = await generatePDF();
      if (pdf) {
        pdf.save(`invoice_${invoice.invoice_number}.pdf`);
        toast.success("Invoice downloaded successfully!");
      }
    } catch (err) {
      console.error("Error generating PDF:", err);
      toast.error("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);

  const getDisplayServiceType = (item) => {
    if (
      (item.service_type === "Other Services" || item.service_type === "Commerce") &&
      item.custom_service
    ) {
      return item.custom_service;
    }
    return item.service_type;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-500">Loading invoice...</div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <h1 className="text-xl font-semibold text-slate-900 mb-2">Invoice Not Found</h1>
            <p className="text-slate-500 text-sm">
              The invoice you're looking for doesn't exist or may have been removed.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-slate-50 py-6 sm:py-10"
      data-testid="public-invoice-page"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Action bar */}
        <div className="flex items-center justify-between mb-6 no-print">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Invoice {invoice.invoice_number}
            </h1>
            <p className="text-slate-500 text-sm">
              {format(new Date(invoice.invoice_date), "PPP")}
            </p>
          </div>
          <Button
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="bg-[#0047FF] hover:bg-[#0033CC] btn-interactive"
            data-testid="download-pdf-btn"
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Download PDF
          </Button>
        </div>

        {/* Invoice document */}
        <Card
          ref={printRef}
          className="border-slate-200 invoice-preview shadow-lg bg-white"
          data-testid="invoice-document"
        >
          <CardContent className="p-8 lg:p-12">
            <div className="text-center mb-10 pb-8 border-b-2 border-slate-200">
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">
                Pokus Technologies Private Limited
              </h1>
              <p className="text-slate-500 text-sm mt-1">GSTIN: 29AAQCP4908B1Z8</p>
              <p className="text-slate-500 text-lg mt-2 tracking-widest uppercase">INVOICE</p>
            </div>

            <div className="grid grid-cols-2 gap-8 mb-10">
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                    Bill To
                  </p>
                  <p className="text-lg font-semibold text-slate-900">{invoice.customer_name}</p>
                  <p className="text-slate-600">{invoice.phone}</p>
                </div>
                {invoice.contact_id && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                      Contact ID
                    </p>
                    <p className="font-mono text-slate-700">{invoice.contact_id}</p>
                  </div>
                )}
              </div>

              <div className="text-right space-y-4">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                    Invoice Number
                  </p>
                  <p className="text-lg font-mono font-semibold text-slate-900">
                    {invoice.invoice_number}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
                    Invoice Date
                  </p>
                  <p className="text-slate-700">
                    {format(new Date(invoice.invoice_date), "PPP")}
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-10 overflow-x-auto -mx-4 sm:mx-0">
              <table className="w-full min-w-[400px]">
                <thead>
                  <tr className="border-b-2 border-slate-200">
                    <th className="text-left py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Service Type
                    </th>
                    <th className="text-center py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Quantity
                    </th>
                    <th className="text-right py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Price/Unit
                    </th>
                    <th className="text-right py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Total (Incl. GST)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.line_items.map((item, index) => (
                    <tr
                      key={item.id || index}
                      className="border-b border-slate-100"
                      data-testid={`invoice-line-item-${index}`}
                    >
                      <td className="py-4 text-slate-900 font-medium">
                        {getDisplayServiceType(item)}
                      </td>
                      <td className="py-4 text-center font-mono text-slate-700">
                        {item.quantity}
                      </td>
                      <td className="py-4 text-right font-mono text-slate-700">
                        {formatCurrency(item.price_per_unit)}
                      </td>
                      <td className="py-4 text-right font-mono font-medium text-slate-900">
                        {formatCurrency(item.line_total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end mb-10">
              <div className="w-full max-w-xs space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Subtotal (Incl. GST)</span>
                  <span className="font-mono text-slate-900">{formatCurrency(invoice.subtotal)}</span>
                </div>
                {invoice.discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Discount</span>
                    <span className="font-mono text-red-500">-{formatCurrency(invoice.discount)}</span>
                  </div>
                )}
                {invoice.delivery_fee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Delivery Fee</span>
                    <span className="font-mono text-slate-900">+{formatCurrency(invoice.delivery_fee)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xl font-bold pt-4 border-t-2 border-slate-200">
                  <span className="text-slate-900">Total Payable</span>
                  <span className="font-mono text-[#0047FF]">{formatCurrency(invoice.total_payable)}</span>
                </div>
              </div>
            </div>

            {invoice.payment_link && (
              <div className="text-center pt-8 border-t border-slate-200">
                <p className="text-sm font-semibold text-slate-700 mb-3">Scan to Pay</p>
                {qrCodeDataUrl && (
                  <img
                    src={qrCodeDataUrl}
                    alt="Payment QR Code"
                    className="w-28 h-28 mx-auto mb-3"
                  />
                )}
                <a
                  href={invoice.payment_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#0047FF] hover:underline text-sm font-medium break-all"
                  data-testid="payment-link"
                >
                  {invoice.payment_link}
                </a>
              </div>
            )}

            <div className="mt-12 pt-8 border-t border-slate-200 text-center text-sm text-slate-400">
              <p>Thank you for your business!</p>
              <p className="mt-1">Pokus Technologies Private Limited</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
